<script setup>
import { ref, computed } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import FormButton from '../Atoms/FormButton.vue'
import IconBase from '../icon/IconBase.vue'
import IconCross from '../icon/IconCross.vue'
import IconUpload from '../icon/IconUpload.vue'
import IconFolderTab from '../icon/IconFolderTab.vue'
import IconPlaylistEmptyPlaceholderImage from '../icon/IconPlaylistEmptyPlaceholderImage.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import { VIDEO_UPLOAD_ACCEPT, MP4_ONLY_ERROR_MESSAGE, isAllowedVideoFile } from '@/Utils/videoUploadFormats'

const props = defineProps({
	visible: { type: Boolean, default: false },
	directories: { type: Array, default: () => [] },
})

const emit = defineEmits(['close', 'upload'])

const { notify } = useNotification()

const selectedDir = ref(null)
const selectedFiles = ref([])
const dragging = ref(false)
const fileInput = ref(null)
const dropdownRef = ref(null)

const selectedDirName = computed(() => selectedDir.value?.name || 'Select folder')

const selectDir = (dir) => {
	selectedDir.value = dir
	dropdownRef.value?.hideDropDownMenu()
}

const addFiles = (fileList) => {
	const all = Array.from(fileList || [])
	if (!all.length) return
	const incoming = all.filter((f) => isAllowedVideoFile(f))
	// Don't silently drop non-MP4 files — tell the user why they were rejected.
	if (incoming.length !== all.length) {
		notify({ group: 'error', text: MP4_ONLY_ERROR_MESSAGE })
	}
	if (!incoming.length) return
	// de-dupe by name + size to avoid double-adding the same file
	const existing = new Set(
		selectedFiles.value.map((f) => `${f.name}::${f.size}`),
	)
	for (const file of incoming) {
		const key = `${file.name}::${file.size}`
		if (!existing.has(key)) {
			selectedFiles.value.push(file)
			existing.add(key)
		}
	}
}

const onFileInputChange = (e) => {
	addFiles(e.target.files)
	e.target.value = null
}

const openFileBrowser = () => {
	fileInput.value?.click()
}

const onDragOver = (e) => {
	e.preventDefault()
	dragging.value = true
}

const onDragLeave = () => {
	dragging.value = false
}

const onDrop = (e) => {
	e.preventDefault()
	dragging.value = false
	addFiles(e.dataTransfer?.files)
}

const removeFile = (idx) => {
	selectedFiles.value.splice(idx, 1)
}

const canUpload = computed(() => selectedDir.value && selectedFiles.value.length > 0)

const handleUpload = () => {
	if (!canUpload.value) return
	// Files are physically stored on S3 under the directory id, so the upload
	// pipeline must reference the id (name is only a display label).
	const dirId = selectedDir.value.id || selectedDir.value._id
	for (const file of selectedFiles.value) {
		emit('upload', { file, dirId })
	}
	selectedFiles.value = []
	selectedDir.value = null
	emit('close')
}

const close = () => {
	selectedFiles.value = []
	selectedDir.value = null
	emit('close')
}

// Use decimal (1000) units so size matches Finder / OS file info.
const formatFileSize = (bytes) => {
	if (bytes < 1000) return bytes + ' B'
	if (bytes < 1000 * 1000) return (bytes / 1000).toFixed(1) + ' KB'
	if (bytes < 1000 * 1000 * 1000) return (bytes / (1000 * 1000)).toFixed(1) + ' MB'
	return (bytes / (1000 * 1000 * 1000)).toFixed(2) + ' GB'
}
</script>

<template>
	<teleport to="body">
		<div v-if="props.visible" class="fixed inset-0 z-50 flex items-center justify-center">
			<div class="absolute inset-0 bg-black/60" @click="close" />
			<div class="relative bg-dark-2 rounded-xl w-full max-w-[520px] max-h-[calc(100dvh-2rem)] overflow-y-auto mx-4 shadow-xl">
				<!-- Header -->
				<div class="flex items-start gap-x-3 px-3 pt-3 pb-0">
					<icon-base class="shrink-0 mt-0.5 text-[#FAFAFB]" :width="28" :height="28"><icon-upload /></icon-base>
					<div class="flex-1 min-w-0">
						<h3 class="text-white font-semibold text-base">Upload file</h3>
						<p class="text-sm mt-0.5" style="color: #8794B5;">Select folder and upload the files</p>
					</div>
					<FormButton custom-classes="shrink-0 text-surface-7 hover:text-white transition-colors p-1" @click="close">
						<icon-base width="16" height="16">
							<icon-cross />
						</icon-base>
					</FormButton>
				</div>

				<!-- Divider -->
				<div class="mx-3 my-3 border-t" style="border-color: #343E61;" />

				<!-- Folder dropdown -->
				<div class="px-3">
					<label class="text-sm text-surface-7 mb-1.5 block">Folder</label>
					<DropDownMenu
						ref="dropdownRef"
						type="flat"
						fullWidth
						position="left"
					>
						<template #toggle-button>
							<div class="flex items-center gap-x-2 w-full">
								<icon-base v-if="selectedDir" class="shrink-0 text-dark-9" :width="15" :height="13" width-view-box="15" height-view-box="13"><icon-folder-tab /></icon-base>
								<span class="truncate">{{ selectedDirName }}</span>
							</div>
						</template>
						<template #menu-items>
							<DropDownMenuItem
								v-for="dir in directories"
								:key="dir.id"
								:onClick="() => selectDir(dir)"
								customClasses="h-10 md:h-8 !p-3 md:!p-2 hover:bg-surface-3 hover:text-white text-nowrap leading-none text-base md:text-tiny flex items-center gap-x-2 w-full max-w-[90vw] truncate"
							>
								<icon-base class="shrink-0 text-dark-9" :width="15" :height="13" width-view-box="15" height-view-box="13"><icon-folder-tab /></icon-base>
								{{ dir.name }}
							</DropDownMenuItem>
						</template>
					</DropDownMenu>
				</div>

				<!-- Drop zone -->
				<div class="px-3 mt-4">
					<div
						class="border border-dashed rounded-lg flex flex-col items-center justify-center py-8 transition-colors cursor-pointer"
						:class="dragging ? 'border-main bg-main-transparent' : 'border-surface-4'"
						style="border: 1px dashed rgb(64, 75, 114) !important"
						@dragover="onDragOver"
						@dragleave="onDragLeave"
						@drop="onDrop"
						@click="openFileBrowser"
					>
						<FormButton type="secondary" @click.stop="openFileBrowser">Select file</FormButton>
						<p class="text-sm text-surface-8 mt-3">Choose a file or drag & drop it here</p>
						<p class="text-xs text-surface-6 mt-1">Supported format: .mp4</p>
					</div>
					<!-- Selected files preview -->
					<div v-if="selectedFiles.length" class="mt-3 flex flex-col gap-2 max-h-[220px] overflow-y-auto">
						<div
							v-for="(file, idx) in selectedFiles"
							:key="file.name + '-' + file.size + '-' + idx"
							class="border rounded-lg px-3 py-2 flex items-center gap-x-3"
							style="border: 1px dashed rgb(64, 75, 114)!important"
						>
							<icon-base class="shrink-0" :width="24" :height="24" width-view-box="48" height-view-box="48" fill="none"><icon-playlist-empty-placeholder-image /></icon-base>
							<div class="flex-1 min-w-0">
								<p class="text-sm text-white truncate" :title="file.name">{{ file.name }}</p>
								<p class="text-xs text-surface-6">{{ formatFileSize(file.size) }}</p>
							</div>
						<FormButton custom-classes="shrink-0 text-surface-7 hover:text-white transition-colors" @click="removeFile(idx)">
							<icon-base width="14" height="14">
								<icon-cross />
							</icon-base>
						</FormButton>
						</div>
					</div>
				</div>

				<input ref="fileInput" type="file" :accept="VIDEO_UPLOAD_ACCEPT" multiple style="display: none;" @change="onFileInputChange" />

				<!-- Footer -->
				<div class="px-3 pt-4 pb-3 flex justify-end">
					<FormButton class="w-full justify-center sm:w-auto" :disabled="!canUpload" @click="handleUpload">
						{{ selectedFiles.length > 1 ? `Upload ${selectedFiles.length} files` : 'Upload file' }}
					</FormButton>
				</div>
			</div>
		</div>
	</teleport>
</template>
